/**
 * Authentication API
 *
 * API functions for admin authentication.
 */

import { httpClient } from "@/lib/http";
import { API_CONFIG, AUTH_ENDPOINTS } from "@/config/api";
import type {
    ApiResponse,
    LoginRequest,
    LoginResponse,
    ForgotPasswordRequest,
    ForgotPasswordResponse,
    VerifyResetCodeRequest,
    VerifyResetCodeResponse,
    ConfirmResetPasswordRequest,
    ConfirmResetPasswordResponse,
    AuthUser,
} from "@/features/auth/types";

// ============================================================================
// API Functions
// ============================================================================

/**
 * Admin login
 *
 * @param payload - Email and password
 * @returns Login response with tokens and user data
 */
export const login = async (payload: LoginRequest): Promise<LoginResponse> => {
    const { data } = await httpClient.post<LoginResponse>(
        AUTH_ENDPOINTS.ADMIN_LOGIN,
        payload,
        { timeout: API_CONFIG.AUTH_TIMEOUT }
    );
    return data;
};

export const getProfile = async (): Promise<ApiResponse<AuthUser>> => {
    const { data } = await httpClient.get<ApiResponse<AuthUser>>(AUTH_ENDPOINTS.PROFILE);
    return data;
};

/**
 * Request password reset code
 *
 * Sends a 6-digit reset code to the provided email.
 * Response is intentionally vague for security (prevents email enumeration).
 *
 * @param payload - Email address
 */
export const forgotPassword = async (
    payload: ForgotPasswordRequest
): Promise<ForgotPasswordResponse> => {
    const { data } = await httpClient.post<ForgotPasswordResponse>(
        AUTH_ENDPOINTS.FORGOT_PASSWORD,
        payload
    );
    return data;
};

/**
 * Verify password reset code
 *
 * @param payload - Email and 6-digit code
 * @returns Verification token for password reset
 */
export const verifyResetCode = async (
    payload: VerifyResetCodeRequest
): Promise<VerifyResetCodeResponse> => {
    const { data } = await httpClient.post<VerifyResetCodeResponse>(
        AUTH_ENDPOINTS.VERIFY_RESET_CODE,
        payload
    );
    return data;
};

/**
 * Confirm password reset
 *
 * Password requirements:
 * - Minimum 8 characters
 * - At least one uppercase letter
 * - At least one lowercase letter
 * - At least one digit
 * - At least one special character (@$!%*?&)
 *
 * Note: All active sessions are revoked after password reset.
 *
 * @param payload - Verification token and new password
 */
export const confirmResetPassword = async (
    payload: ConfirmResetPasswordRequest
): Promise<ConfirmResetPasswordResponse> => {
    const { data } = await httpClient.post<ConfirmResetPasswordResponse>(
        AUTH_ENDPOINTS.CONFIRM_RESET_PASSWORD,
        payload
    );
    return data;
};
